/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import Contentful from '@svg/contentful';
import styled from 'styled-components';

import { Button, Modal } from '@common/index';
import { Alert, LinearProgress, Stack, Step, StepContent, StepLabel, Stepper, Typography, alpha } from '@mui/material';
import { DocumentLoader } from '@common/hooks/useDocument';
import { useParams } from 'react-router-dom';
import { ContentfulArticle, DocumentLoaders } from '@common/hooks/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enableBucketEmbeddings, TelnyxBucketFileEmbedding } from '@requests/buckets';
import { bucketActions } from '@store/slices';
import { useDispatch, useSelector } from 'react-redux';
import { ContentfulLoaderCollection, storeLoader } from '@requests/loaders';
import { State } from '@store/index';
import { useTheme } from '../../../../styles';
import { LinearProgressWithLabel, UpsertStep } from '@common/Loaders/LinearProgress';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 6px;

  white-space: pre-wrap;
  p {
    color: ${({ theme }) => alpha(theme.text.standard, 0.6)};
    font-size: 14px;
  }

  gap: 1.5rem;
`;

interface Props {
  open: string | null;
  page: number;
  setOpen: React.Dispatch<React.SetStateAction<string | null>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  reset: () => void;
  configuration: {
    secretIdentifier: string;
    selected: ContentfulLoaderCollection[];
    documentLoader: DocumentLoader;
    region: string;
    refreshInterval: string;
    unit: string;
    activeStep: number;
    setActiveStep: React.Dispatch<React.SetStateAction<number>>;
    space: string;
    filteredDocuments: number;
    setFilteredDocuments: React.Dispatch<React.SetStateAction<number>>;
  };
}

export const ContentfulFiles: React.FC<Props> = ({ open, page, reset, configuration }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const username = useSelector((state: State) => state.auth.username);

  const { service_id, bucketName } = useParams();

  const {
    secretIdentifier,
    selected,
    documentLoader,
    region,
    refreshInterval,
    unit,
    activeStep,
    setActiveStep,
    space,
    filteredDocuments,
    setFilteredDocuments,
  } = configuration;

  const { load, documents, loading, addDocumentsToQueue } = documentLoader;

  const [error, setError] = useState<string | null>(null);

  const embeddingQueryKey = ['globalFiles', 'embedding-files', { service_id, bucket_name: bucketName }];
  const embeddingFiles = queryClient.getQueryData<TelnyxBucketFileEmbedding[]>(embeddingQueryKey) || [];

  const { mutateAsync: embed } = useMutation({
    mutationFn: enableBucketEmbeddings,
    onSuccess: (data) => {
      if (!data) return;

      dispatch(bucketActions.setTaskId(data.task_id));
      queryClient.invalidateQueries({ queryKey: ['globalFiles'] });
    },
  });

  const { mutateAsync: store } = useMutation({
    mutationFn: storeLoader,
    onSuccess: (data) => {
      if (!data) return;
      queryClient.invalidateQueries({ queryKey: ['globalFiles', 'bucket-loader'] });
    },
  });

  const onComplete = (docs: ContentfulArticle[]) => {
    if (docs.length === 0)
      return setError(
        'There was no content found for the content models selected. Please ensure you select at least one content model that has content.'
      );

    const files: File[] = docs.map((doc) => {
      const fileContent = doc.file;
      const blob = new Blob([fileContent], { type: 'text/markdown' });
      const fileName = doc.filename;
      const file = new File([blob], fileName, { type: 'text/markdown' });
      return file;
    });

    addDocumentsToQueue(service_id as string, bucketName as string, region, files, null, onUploadComplete);
    setFilteredDocuments(docs.length || 0);
    setActiveStep(1);
  };

  const onUploadComplete = () => {
    setActiveStep(2);
    embed({ bucketName, model: 'thenlper/gte-large', loader: 'default', service_id: service_id as string });

    const loader = {
      bucket_name: bucketName as string,
      service_id: service_id as string,
      loader: 'contentful',
      auth_secret_name: secretIdentifier,
      loader_settings: JSON.stringify({ collections: selected.map((sel) => ({ id: sel.id, name: sel.name })), space }),
      refresh_interval: refreshInterval,
      refresh_interval_config: JSON.stringify({ value: refreshInterval, unit }),
      last_refreshed: new Date().toISOString(),
      user_id: username as string,
    };

    store({ service_id: service_id as string, loader });
  };

  // start loading when the modal is opened
  useEffect(() => {
    const loader = DocumentLoaders.Contentful;
    if (!loading)
      load(
        loader,
        service_id as string,
        secretIdentifier,
        onComplete,
        space,
        selected.map((sel) => sel.id)
      );
  }, []);

  const conditionalSteps: UpsertStep[] = [
    {
      label: 'Upload Files to Bucket',
      description: 'The published content is now being uploaded to the bucket.',
      fraction: { numerator: filteredDocuments - documents.length, denominator: filteredDocuments },
    },
    {
      label: 'Embed Files in Bucket',
      description:
        'The published content in the bucket is now being embedded. This process is expected to take quite some time, but you can close the modal at this point and view the progress in the queue.',
      fraction: {
        numerator: embeddingFiles.filter((file) => file.status === 'success').length,
        denominator: filteredDocuments,
      },
    },
  ];

  const steps: UpsertStep[] = [
    {
      label: 'Load Contentful Content Models',
      description: `The information from the content models selected is now being loaded from the Contentful API.`,
    },
    ...(error ? [] : conditionalSteps),
  ];

  const body = (
    <Container>
      {error ? (
        <Alert severity='error'>{error}</Alert>
      ) : (
        <p>
          The information from the content models selected is now being loaded into the bucket for the first time. This
          process may take some time to complete.
        </p>
      )}
      <Stepper activeStep={activeStep} orientation='vertical'>
        {steps.map((step) => (
          <Step key={step.label}>
            <StepLabel error={Boolean(error)}>{step.label}</StepLabel>
            <StepContent>
              <Stack spacing='8px'>
                {!error && step.fraction && (
                  <LinearProgressWithLabel
                    denominator={step.fraction.denominator}
                    numerator={step.fraction.numerator}
                  />
                )}
                {!error && !step.fraction && <LinearProgress />}
                <Typography>{error ? 'An error occured on this step.' : step.description}</Typography>
              </Stack>
            </StepContent>
          </Step>
        ))}
      </Stepper>
    </Container>
  );

  const actions = (
    <Button
      disabled={activeStep < 2}
      customColor='primary'
      onClick={() => {
        reset();
        setError(null);
      }}
    >
      Close
    </Button>
  );

  const errorActions = (
    <Stack direction='row' spacing={1}>
      <Button
        onClick={() => {
          reset();
          setError(null);
        }}
      >
        Close
      </Button>
    </Stack>
  );

  return (
    <Modal
      open={open === 'contentful' && page === 3}
      setOpen={reset}
      onClose={() => {
        if (activeStep < 2) return;
        reset();
        setError(null);
      }}
      page={page + 1}
      total_pages={4}
      background={alpha(theme.text.standard, 0.12)}
      header='Contentful'
      icon={<Contentful color={theme.text.standard} height={22} />}
      body={body}
      actions={error ? errorActions : actions}
    />
  );
};
